import React, { memo } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { formatCurrency } from '../../utils/formatting'

/**
 * Optimierte PropertyCard-Komponente mit React.memo
 * 
 * Verhindert unnötige Re-Renders wenn sich die Property-Daten nicht geändert haben.
 * Wichtig für große Listen von Immobilien.
 */
const PropertyCard = memo(({ 
  property, 
  onPurchase, 
  formatCurrency: formatCurrencyProp,
  getPropertyTypeIcon,
  unlockedTalents = [],
  calculateAverageRentPerSqm,
  // Watchlist (Quick Win - für alle verfügbar)
  isInWatchlist = false,
  onAddToWatchlist,
  onRemoveFromWatchlist,
}) => {
  const format = formatCurrencyProp || formatCurrency
  
  const roi = property.roi 
    ? (property.roi * 100).toFixed(1) 
    : ((property.miete * 12) / property.wert * 100).toFixed(1)
  
  const roiColor = roi >= 8 
    ? 'text-emerald-400' 
    : roi >= 5 
      ? 'text-amber-400' 
      : 'text-slate-400'
  
  return (
    <div
      className="rounded-2xl bg-slate-900/60 border border-white/5 p-4 hover:border-accent/50 transition hover:shadow-lg hover:shadow-accent/10"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {getPropertyTypeIcon?.(property.typ)}
            <p className="text-xs text-slate-500 uppercase">{property.stadtteil}</p>
            {/* Watchlist-Button - Quick Win: Für alle verfügbar */}
            {onAddToWatchlist && onRemoveFromWatchlist && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  if (isInWatchlist) {
                    onRemoveFromWatchlist()
                  } else {
                    onAddToWatchlist()
                  }
                }}
                className={`ml-auto p-1.5 rounded-lg transition ${
                  isInWatchlist
                    ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/40'
                    : 'bg-slate-800/50 text-slate-400 hover:bg-slate-700/50 border border-white/5 hover:border-blue-500/40'
                }`}
                title={isInWatchlist ? 'Von Watchlist entfernen' : 'Zur Watchlist hinzufügen'}
              >
                {isInWatchlist ? <Eye size={14} /> : <EyeOff size={14} />}
              </button>
            )}
          </div>
          <h4 className="text-lg text-slate-100 font-semibold">{property.typ}</h4>
          <p className="text-xs text-slate-500 mt-1">
            {property.hatBestandsmieter 
              ? 'Bestandsmieter vorhanden' 
              : 'Leerstehend – frei zur Vermietung'}
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm text-slate-400">Preis</p>
          <p className="text-xl font-bold text-slate-50">{format(property.wert)}</p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-4 gap-2 text-sm">
        <div>
          <p className="text-slate-500 text-xs">Miete</p>
          <p className="text-emerald-400 font-medium">{format(property.miete)}</p>
          <p className="text-xs text-slate-500">/Monat</p>
        </div>
        <div>
          <p className="text-slate-500 text-xs">QM</p>
          <p className="text-slate-300 font-medium">{property.area ?? 'N/A'} m²</p>
          {property.area && property.miete ? (
            <div className="relative group">
              <p className="text-xs text-slate-500 cursor-help">
                {format(property.miete / property.area)}/m²
              </p>
              {unlockedTalents.includes('rental-11') && (
                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-48 p-2 rounded-lg bg-slate-900 border border-accent/40 shadow-lg text-xs">
                  <div className="text-slate-300 font-semibold mb-1">Mietpreis-Analyse</div>
                  <div className="text-slate-400 space-y-1">
                    <div className="flex justify-between">
                      <span>Aktuell:</span>
                      <span className="text-slate-300">{format(property.miete / property.area)}/m²</span>
                    </div>
                    <div className="flex justify-between border-t border-white/10 pt-1">
                      <span>Durchschnitt:</span>
                      <span className="text-emerald-400 font-semibold">
                        {format(calculateAverageRentPerSqm?.(property) || 0)}/m²
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1 pt-1 border-t border-white/5">
                      Potenzial bei Neuvermietung
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-500">
              {property.pricePerSqm ? `${format(property.pricePerSqm)}/m²` : ''}
            </p>
          )}
        </div>
        <div>
          <p className="text-slate-500 text-xs">Nebenkosten</p>
          <p className="text-slate-300">{format(property.monatlicheKosten)}</p>
        </div>
        <div>
          <p className="text-slate-500 text-xs">ROI</p>
          <p className={`${roiColor} font-semibold`}>{roi}%</p>
          <p className="text-xs text-slate-500">p.a.</p>
        </div>
      </div>
      <button
        onClick={() => onPurchase?.(property)}
        className="mt-4 w-full rounded-xl bg-accent/80 py-2.5 text-slate-900 font-semibold hover:bg-accent transition shadow-lg shadow-accent/20"
      >
        Kaufen
      </button>
    </div>
  )
}, (prevProps, nextProps) => {
  // Custom comparison function für React.memo
  // Vergleicht nur die relevanten Property-Daten
  return (
    prevProps.property.id === nextProps.property.id &&
    prevProps.property.wert === nextProps.property.wert &&
    prevProps.property.miete === nextProps.property.miete &&
    prevProps.property.area === nextProps.property.area &&
    prevProps.property.hatBestandsmieter === nextProps.property.hatBestandsmieter &&
    prevProps.property.stadtteil === nextProps.property.stadtteil &&
    prevProps.property.typ === nextProps.property.typ &&
    prevProps.unlockedTalents?.length === nextProps.unlockedTalents?.length &&
    prevProps.isInWatchlist === nextProps.isInWatchlist
  )
})

PropertyCard.displayName = 'PropertyCard'

export default PropertyCard
